import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SubmitToFinancasDialog } from '../FinancasOfficePicker';
import { errorMessageFrom } from '../../api/financasOffices';
import CaseAttribution, { CaseAttributionData, attributionSearchTerms } from '../CaseAttribution';
import CaseTenantBadge, { CaseTenantData } from '../CaseTenantBadge';
import MinorTag from '../MinorTag';
import FinancasWaitingBadge, {
  FinancasNewBadge,
  FinancasWaitingData,
  hasNewFromFinancas,
} from '../FinancasWaitingBadge';
import { DAY_MS, elapsedSeverityClass, formatElapsed } from '../../utils/elapsed';

interface Case {
  id: string;
  name: string;
  created_at: any;
  lawyer_review_at?: any;
  service: string;
  lawyer_status: string;
  status?: string;
  first_name?: string;
  last_name?: string;
  user_email?: string;
  user_display_name?: string;
  financas_office_id?: string | null;
  financas_office_name?: string | null;
  financas_reference?: string | null;
  lawyer_submitted_at?: any;
  lawyer_status_updated_at?: any;
  attribution?: CaseAttributionData;
  /** Present only on white-label cases. Absent means Coepi. */
  tenant?: CaseTenantData;
  /** "minor" for a NIF for a child. Absent or "adult" on every other case. */
  applicant_type?: string | null;
  /** What the e-balcão reader found. Absent when nobody has read the request. */
  financas_waiting?: FinancasWaitingData | null;
}

const normalizeStatus = (status?: string) => {
  if (!status) {
    return '';
  }
  return status.trim().replace(/[-\s]/g, '_').toUpperCase();
};

// Parked until the customer sends what the lawyer needs to move on (a clearer
// scan, a missing detail). Stored verbatim in lawyer_status like the other
// statuses, and doubles as the key of its tab.
const WAITING_ON_CUSTOMER = 'Waiting on customer';

// A NIF case waiting for its POA certification, before the lawyer works it. The
// backend presents it as this status and never stores it in lawyer_status, so
// moving a case in or out sends the customer no email. The certification moves
// the case to Open by itself.
const CERTIFICATE = 'Certificate';

// Not a lawyer_status: a tab over what the e-balcão reader found in the portal.
// A case lands here because the last message in its request asks us for
// something, whatever status the case itself carries.
const FINANCAS_ASKED = 'Finanças asked';

const isAwaitingFinancas = (caseItem: Case): boolean =>
  caseItem.financas_waiting?.awaiting_lawyer === true;

// Also not a lawyer_status: the cases where Finanças wrote something she has
// not opened yet, any message at all. The reader marks every thread read in
// the portal, so this is the "unread" the portal no longer gives her.
const FINANCAS_NEW = 'New from Finanças';

const hasNewFinancasMessages = (caseItem: Case): boolean =>
  hasNewFromFinancas(caseItem.financas_waiting);

const isOpenWorkflowStatus = (status?: string) => {
  const normalizedStatus = normalizeStatus(status);
  return normalizedStatus === 'LAWYER_REVIEW'
    || normalizedStatus === 'USER_COMPLETED'
    || normalizedStatus === 'PASSPORT_VIDEO_VALIDATION'
    || normalizedStatus === 'UPLOAD_PASSPORT_COPY'
    || normalizedStatus === 'COMPLETED';
};

const shouldShowInOpenTab = (caseItem: Case) => {
  const normalizedLawyerStatus = normalizeStatus(caseItem.lawyer_status || 'Open');
  if (
    normalizedLawyerStatus === 'COMPLETE'
    || normalizedLawyerStatus === 'SUBMITTED'
    || normalizedLawyerStatus === 'CLOSED'
    // Nothing for the lawyer to do until the customer answers.
    || normalizedLawyerStatus === normalizeStatus(WAITING_ON_CUSTOMER)
    // Nothing for the lawyer to do until the certifier has signed.
    || normalizedLawyerStatus === normalizeStatus(CERTIFICATE)
  ) {
    return false;
  }
  if (normalizedLawyerStatus === 'OPEN') {
    return true;
  }
  const workflowLooksOpen = isOpenWorkflowStatus(caseItem.status);
  if (workflowLooksOpen) {
    return true;
  }
  return !caseItem.status;
};

const toMs = (timestamp: any): number => {
  if (!timestamp) return 0;
  if (timestamp._seconds) return timestamp._seconds * 1000;
  const time = new Date(timestamp).getTime();
  return isNaN(time) ? 0 : time;
};

// When the case landed in the review queue. created_at is when the CUSTOMER
// opened their application, which can be days earlier: one case sat at
// "Open 19h" and coloured as aging the moment it first became visible, because
// the customer took a night to finish uploading. Age from the handover instead,
// falling back to created_at for cases that predate the lawyer_review_at stamp.
const reviewSinceMs = (caseItem: Case): number =>
  toMs(caseItem.lawyer_review_at) || toMs(caseItem.created_at);

// Cases submitted before lawyer_submitted_at existed only carry the time of their
// last status change, which for a case still in Submitted is the submission.
const submittedSinceMs = (caseItem: Case): number =>
  toMs(caseItem.lawyer_submitted_at) || toMs(caseItem.lawyer_status_updated_at);

// A case still waiting on the customer last changed status when it was parked,
// so that change is when the wait started.
const waitingSinceMs = (caseItem: Case): number =>
  toMs(caseItem.lawyer_status_updated_at);

// The name to identify the case by. first_name/last_name come off the identity
// document, which is the name on the paperwork the lawyer is matching against,
// and they are the best populated (74 of 77 open cases, vs 48 for the profile
// display name). Fall back to the display name, then the email, which is the
// only field present on every case.
const clientName = (caseItem: Case): string => {
  const documentName = [caseItem.first_name, caseItem.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return documentName || caseItem.user_display_name || caseItem.user_email || '\u2014';
};

// A case counts as "open and not yet submitted by us" when the lawyer-facing
// status hasn't moved on to Submitted / Complete / Closed.
const isNotYetSubmitted = (caseItem: Case): boolean => {
  const normalized = normalizeStatus(caseItem.lawyer_status || 'Open');
  return (
    normalized !== 'SUBMITTED' &&
    normalized !== 'COMPLETE' &&
    normalized !== 'CLOSED'
  );
};

// Whole calendar days since the case was marked Submitted, for the Submitted
// tab. Finanças turnaround is measured in days, so hours would only be noise.
const daysSince = (fromMs: number, nowMs: number): number =>
  Math.max(0, Math.floor((nowMs - fromMs) / DAY_MS));

const SKELETON_ROWS = 6;

// Placeholder rows shown until the first /cases response arrives. They use the
// real row classes so the grid tracks match the header and nothing jumps when
// the data replaces them.
const CaseListSkeleton: React.FC<{ showPartnerColumn: boolean; showAgeColumn: boolean }> = ({
  showPartnerColumn,
  showAgeColumn,
}) => (
  <div role="status" aria-label="Loading cases">
    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
      <div
        key={i}
        className={`case-list-item${showPartnerColumn ? ' case-list-row--partner' : ''}${
          showAgeColumn ? '' : ' case-list-row--no-age'
        }`}
        aria-hidden="true"
      >
        <div><span className="skeleton-bar skeleton-shimmer" style={{ width: '70%' }} /></div>
        <div><span className="skeleton-bar skeleton-shimmer" style={{ width: `${55 + ((i * 17) % 30)}%` }} /></div>
        <div><span className="skeleton-pill skeleton-shimmer" /></div>
        <div><span className="skeleton-bar skeleton-shimmer" style={{ width: '80%' }} /></div>
        <div><span className="skeleton-bar skeleton-shimmer" style={{ width: '40%' }} /></div>
        {showPartnerColumn && <div><span className="skeleton-bar skeleton-shimmer" style={{ width: '60%' }} /></div>}
        {showAgeColumn && <div><span className="skeleton-pill skeleton-shimmer" /></div>}
        <div><span className="skeleton-select skeleton-shimmer" /></div>
        <div><span className="skeleton-button skeleton-shimmer" /></div>
      </div>
    ))}
  </div>
);

interface CasesPageProps {
  /**
   * 'partner' pins the page to white-label cases and drops the picker.
   *
   * It exists because "is this ours or a partner's?" is the first thing the
   * lawyer needs to know: a partner's customer has never heard of Coepi, so
   * replying in our name undoes the white label. The badge answers that per
   * row; this answers it for the whole screen.
   */
  scope?: 'all' | 'partner';
}

const CasesPage: React.FC<CasesPageProps> = ({ scope = 'all' }) => {
  const [cases, setCases] = useState<Case[]>([]);
  // True until the first /cases response lands. Without it the page renders
  // "Open (0)" and "No cases to display." for the seconds the request takes,
  // which reads as an empty queue. Refetches after a status change keep the
  // current list on screen instead of flashing back to the skeleton.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('Open');
  const [serviceFilter, setServiceFilter] = useState('all');
  // 'all' | 'adult' | 'minor'. A case without applicant_type is an adult's.
  const [applicantFilter, setApplicantFilter] = useState('all');
  const hasMinorCases = cases.some((c) => c.applicant_type === 'minor');
  // 'all' | 'coepi' | 'partner' | 'tenant:<id>'. On the partner view it
  // starts at 'partner', which reads as "all partners": that page can narrow
  // to one partner but can never widen to ours.
  const [originFilter, setOriginFilter] = useState(scope === 'partner' ? 'partner' : 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusError, setStatusError] = useState<{ caseId: string; message: string } | null>(null);
  const [submitDialogCase, setSubmitDialogCase] = useState<Case | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Re-render once a minute so the "open for Xd Xh Xm" badges stay current.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCases = async () => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken();
    fetch(`${process.env.REACT_APP_API_URL}/cases`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => {
        // fetch resolves on 401/500 too; without this a failed load would
        // render as an empty queue.
        if (response.ok === false) throw new Error(`Failed to load cases (${response.status})`);
        return response.json();
      })
      .then(data => {
        setCases(data.cases || []);
        setLoadError(false);
      })
      .catch(error => {
        console.error('Error fetching cases:', error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (currentUser) {
      fetchCases();
    }
  }, [currentUser]);

  const handleStatusChange = async (caseId: string, status: string) => {
    if (!currentUser) return;
    setStatusError(null);
    // Marking Submitted needs the Finanças office; ask for it when the case
    // has none. The dialog sends the status update itself.
    const caseItem = cases.find(c => c.id === caseId);
    if (
      status === 'Submitted'
      && caseItem
      && caseItem.lawyer_status !== 'Submitted'
      && !caseItem.financas_office_id
    ) {
      setSubmitDialogCase(caseItem);
      return;
    }
    const token = await currentUser.getIdToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) {
        setStatusError({
          caseId,
          message: errorMessageFrom(data, 'Failed to update the case status.'),
        });
      }
      fetchCases();
    } catch (error) {
      console.error('Error updating case status:', error);
      setStatusError({ caseId, message: 'Failed to update the case status. Please try again.' });
    }
  };

  const handleDownloadFiles = async (caseId: string) => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/documents/zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseId}_documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const matchesSearch = (caseItem: Case) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return true;
    }
    const fields = [
      caseItem.first_name,
      caseItem.last_name,
      caseItem.user_display_name,
      caseItem.user_email,
      `${caseItem.first_name || ''} ${caseItem.last_name || ''}`,
      // "show me everything Lisbon Relocations sent" and "who used SUMMER20"
      // are questions about a partner or a code, not about a name.
      ...attributionSearchTerms(caseItem.attribution),
      caseItem.tenant?.tenantName ?? '',
      caseItem.tenant?.tenantId ?? '',
      // The number Finanças quotes in its emails. Matching one to a case is
      // the reason the reference is recorded at all.
      caseItem.financas_reference ?? '',
    ];
    return fields.some(field => field && field.toLowerCase().includes(query));
  };

  // Whose case this is. Deliberately applied before the tab counts as well as
  // the rows, so "Open (3)" on the partner view means three partner cases and
  // not three of everything.
  const effectiveOrigin =
    scope === 'partner' && !originFilter.startsWith('tenant:') ? 'partner' : originFilter;
  const inScope = (caseItem: Case): boolean => {
    if (effectiveOrigin === 'all') return true;
    if (effectiveOrigin === 'coepi') return !caseItem.tenant;
    if (effectiveOrigin === 'partner') return Boolean(caseItem.tenant);
    if (effectiveOrigin.startsWith('tenant:')) {
      return caseItem.tenant?.tenantId === effectiveOrigin.slice('tenant:'.length);
    }
    return true;
  };
  const scopedCases = cases.filter(inScope);

  // A column only where every visible row would fill it. On the general page
  // that is a column of blanks, which is why the badge exists there instead.
  const showPartnerColumn =
    effectiveOrigin === 'partner' || effectiveOrigin.startsWith('tenant:');

  // One entry per partner that actually has a case here, so the picker does
  // not list partners the lawyer has never seen.
  const partnerOptions = Array.from(
    new Map(
      cases
        .filter(c => c.tenant)
        .map(c => [c.tenant!.tenantId, c.tenant!.tenantName] as const)
    ).entries()
  );

  const filteredCases = scopedCases.filter(caseItem => {
    // Apply search filter (first name, last name, email)
    if (!matchesSearch(caseItem)) {
      return false;
    }
    // Apply service filter
    if (serviceFilter !== 'all' && caseItem.service !== serviceFilter) {
      return false;
    }
    if (applicantFilter !== 'all') {
      const isMinor = caseItem.applicant_type === 'minor';
      if ((applicantFilter === 'minor') !== isMinor) return false;
    }
    if (activeTab === 'Open') {
      return shouldShowInOpenTab(caseItem);
    }
    if (activeTab === FINANCAS_ASKED) {
      return isAwaitingFinancas(caseItem);
    }
    if (activeTab === FINANCAS_NEW) {
      return hasNewFinancasMessages(caseItem);
    }
    return caseItem.lawyer_status === activeTab;
  }).sort((a, b) => reviewSinceMs(a) - reviewSinceMs(b));

  const certificateCasesCount = scopedCases.filter(c => c.lawyer_status === CERTIFICATE).length;
  const openCasesCount = scopedCases.filter(shouldShowInOpenTab).length;
  const waitingCasesCount = scopedCases.filter(c => c.lawyer_status === WAITING_ON_CUSTOMER).length;
  const submittedCasesCount = scopedCases.filter(c => c.lawyer_status === 'Submitted').length;
  const completeCasesCount = scopedCases.filter(c => c.lawyer_status === 'Complete').length;
  const financasAskedCount = scopedCases.filter(isAwaitingFinancas).length;
  const financasNewCount = scopedCases.filter(hasNewFinancasMessages).length;

  // Only the Certificate and Open tabs carry an age: the badge is gated on isNotYetSubmitted, so
  // on Submitted/Complete/Closed the column would be empty on every row. Drop the
  // column there instead of rendering a header over a blank strip.
  const showReviewAge = activeTab === 'Open' || activeTab === CERTIFICATE;
  // The Submitted tab reuses the same column for how long Finanças has had the
  // case and which office it sits at.
  // Every case on the Finanças asked tab is sitting at Finanças too, so it
  // carries the same column: how long they have had it, and where.
  const showFinancasAsked = activeTab === FINANCAS_ASKED;
  const showFinancasNew = activeTab === FINANCAS_NEW;
  const showSubmittedAge = activeTab === 'Submitted' || showFinancasAsked || showFinancasNew;
  // The Waiting on customer tab reuses it for how long the customer has had it.
  const showWaitingAge = activeTab === WAITING_ON_CUSTOMER;
  const showAgeColumn = showReviewAge || showSubmittedAge || showWaitingAge;

  const formatDate = (timestamp: any) => {
    const date = new Date(toMs(timestamp));
    if (isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // A count of 0 while the list is still loading is a lie, so show a
  // placeholder in its place.
  const tabCount = (count: number) =>
    loading ? <span className="tab-count-skeleton skeleton-shimmer" aria-hidden="true" /> : `(${count})`;

  return (
    <>
      <div className="task-view-header">
        <h2>{scope === 'partner' ? 'Partner cases' : 'Cases'}</h2>
        <div className="cases-controls">
          <input
            type="text"
            className="cases-search"
            placeholder="Search by name, email or Finanças reference…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search cases by name, email or Finanças reference"
          />
          <div className="service-filter">
            <select
              className="status-select"
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              aria-label={
                scope === 'partner'
                  ? 'Filter by partner'
                  : 'Filter by who the case belongs to'
              }
            >
              {scope === 'partner' ? (
                <option value="partner">All partners</option>
              ) : (
                <>
                  <option value="all">All cases</option>
                  <option value="coepi">Coepi only</option>
                  <option value="partner">Partners only</option>
                </>
              )}
              {partnerOptions.map(([id, name]) => (
                <option key={id} value={`tenant:${id}`}>{name}</option>
              ))}
            </select>
          </div>
          <div className="service-filter">
          <select
            className="status-select"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="all">All Services</option>
            <option value="NIF Application">NIF Application</option>
            <option value="Passport Certificate">Passport Certificate</option>
          </select>
          </div>
          {/* Only once a child's case exists: the portal looks as before until then. */}
          {(hasMinorCases || applicantFilter !== 'all') && (
          <div className="service-filter">
            <select
              className="status-select"
              value={applicantFilter}
              onChange={(e) => setApplicantFilter(e.target.value)}
              aria-label="Filter by applicant type"
            >
              <option value="all">All applicants</option>
              <option value="adult">Adults</option>
              <option value="minor">Minors</option>
            </select>
          </div>
          )}
        </div>
      </div>
      <div className="tabs">
        <button className={activeTab === CERTIFICATE ? 'active' : ''} onClick={() => handleTabClick(CERTIFICATE)}>Certificate {tabCount(certificateCasesCount)}</button>
        <button className={activeTab === 'Open' ? 'active' : ''} onClick={() => handleTabClick('Open')}>Open {tabCount(openCasesCount)}</button>
        <button className={activeTab === WAITING_ON_CUSTOMER ? 'active' : ''} onClick={() => handleTabClick(WAITING_ON_CUSTOMER)}>Waiting on customer {tabCount(waitingCasesCount)}</button>
        <button className={activeTab === 'Submitted' ? 'active' : ''} onClick={() => handleTabClick('Submitted')}>Submitted {tabCount(submittedCasesCount)}</button>
        {/* Shown at (0) as well: an empty tab is the answer to "is anything
            waiting on me at Finanças?", and it is only true when it is read. */}
        {/* Shown at (0) too, for the same reason: (0) means she has seen
            everything Finanças wrote. */}
        <button
          className={`${showFinancasNew ? 'active' : ''}${financasNewCount > 0 ? ' tab-has-news' : ''}`}
          onClick={() => handleTabClick(FINANCAS_NEW)}
        >
          New from Finanças {tabCount(financasNewCount)}
        </button>
        <button
          className={`${showFinancasAsked ? 'active' : ''}${financasAskedCount > 0 ? ' tab-needs-us' : ''}`}
          onClick={() => handleTabClick(FINANCAS_ASKED)}
        >
          Finanças asked {tabCount(financasAskedCount)}
        </button>
        <button className={activeTab === 'Complete' ? 'active' : ''} onClick={() => handleTabClick('Complete')}>Complete {tabCount(completeCasesCount)}</button>
        <button className={activeTab === 'Closed' ? 'active' : ''} onClick={() => handleTabClick('Closed')}>Closed</button>
      </div>
      <div
        className={`case-list-header${showPartnerColumn ? ' case-list-row--partner' : ''}${
          showAgeColumn ? '' : ' case-list-row--no-age'
        }`}
      >
        <div><strong>Service</strong></div>
        <div><strong>Client</strong></div>
        <div><strong>Applicant</strong></div>
        <div><strong>ID</strong></div>
        <div><strong>Source</strong></div>
        {showPartnerColumn && <div><strong>Partner</strong></div>}
        {showAgeColumn && (
          <div><strong>{showSubmittedAge ? 'At Finanças' : showWaitingAge ? 'With Customer' : 'In Review'}</strong></div>
        )}
        <div><strong>Status</strong></div>
        <div><strong>Actions</strong></div>
      </div>
      {statusError && (
        <div className="status-error-banner" role="alert">
          <span>
            Case <Link to={`/case/${statusError.caseId}`} className="status-error-link">{statusError.caseId}</Link>: {statusError.message}
          </span>
          <button className="status-error-dismiss" onClick={() => setStatusError(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="case-list" aria-busy={loading}>
        {loading ? (
          <CaseListSkeleton
            showPartnerColumn={showPartnerColumn}
            showAgeColumn={showAgeColumn}
          />
        ) : filteredCases.length > 0 ? (
          filteredCases.map(caseItem => (
            <div
              key={caseItem.id}
              className={`case-list-item${showPartnerColumn ? ' case-list-row--partner' : ''}${
                showAgeColumn ? '' : ' case-list-row--no-age'
              }`}
            >
              <div><strong><Link to={`/case/${caseItem.id}`} className="service-link">{caseItem.service}</Link></strong></div>
              <div>
                <span className="case-client" title={clientName(caseItem)}>{clientName(caseItem)}</span>
                {/* Next to the customer's name rather than in its own
                    column: it is a fact about who this person thinks they are
                    dealing with, and it must be impossible to miss while
                    reading their name.

                    Dropped where the Partner column is showing. The badge's
                    signal is that it appears at all, and on a list where
                    every row is a partner case it appears on every row and
                    says nothing. */}
                {!showPartnerColumn && <CaseTenantBadge tenant={caseItem.tenant} />}
                {/* On every other tab: a request Finanças is waiting on is the
                    one thing in this list that needs her today, and it must not
                    be hidden because she is looking at Open. Dropped on its own
                    tab, where it would appear on every row and say nothing. */}
                {!showFinancasAsked && <FinancasWaitingBadge waiting={caseItem.financas_waiting} />}
                {/* Likewise dropped on its own tab. */}
                {!showFinancasNew && <FinancasNewBadge waiting={caseItem.financas_waiting} />}
              </div>
              {/* Adult or Minor: a child's case has two parents signing and the
                  birth certificate in the bundle. */}
              <div><MinorTag applicantType={caseItem.applicant_type} showAdult /></div>
              <div><span className="case-id" title={caseItem.id}>{caseItem.id}</span></div>
              <div><CaseAttribution attribution={caseItem.attribution} /></div>
              {showPartnerColumn && (
                <div>
                  <span className="case-partner" title={caseItem.tenant?.supportEmail || undefined}>
                    {caseItem.tenant?.tenantName ?? '—'}
                  </span>
                </div>
              )}
              {showAgeColumn && (
                <div className="case-review-age-cell">
                  {showReviewAge && isNotYetSubmitted(caseItem) && reviewSinceMs(caseItem) > 0 && (
                    <span
                      className={`case-open-elapsed ${elapsedSeverityClass(reviewSinceMs(caseItem), now)}`}
                      title={`In review since ${formatDate(caseItem.lawyer_review_at || caseItem.created_at)}`}
                    >
                      Open {formatElapsed(reviewSinceMs(caseItem), now)}
                    </span>
                  )}
                  {showSubmittedAge && submittedSinceMs(caseItem) > 0 && (
                    <span
                      className="case-open-elapsed case-submitted-elapsed"
                      title={`Submitted on ${formatDate(caseItem.lawyer_submitted_at || caseItem.lawyer_status_updated_at)}`}
                    >
                      Submitted {daysSince(submittedSinceMs(caseItem), now)}d
                    </span>
                  )}
                  {showWaitingAge && waitingSinceMs(caseItem) > 0 && (
                    <span
                      className="case-open-elapsed case-waiting-elapsed"
                      title={`Waiting on the customer since ${formatDate(caseItem.lawyer_status_updated_at)}`}
                    >
                      Waiting {daysSince(waitingSinceMs(caseItem), now)}d
                    </span>
                  )}
                  {showSubmittedAge && (
                    <span className="case-financas-office" title={caseItem.financas_office_name || undefined}>
                      {caseItem.financas_office_name
                        ? `Filed at: ${caseItem.financas_office_name}`
                        : 'No office recorded'}
                    </span>
                  )}
                  {/* The portal's own word for where the request stands, which
                      is worth a line on the tab where every case is sitting at
                      Finanças. Only on cases the reader has opened: silence
                      here means nobody has looked, not that nothing moved. */}
                  {showSubmittedAge && caseItem.financas_waiting?.state && (
                    <span
                      className="case-financas-portal-state"
                      title={
                        formatDate(caseItem.financas_waiting.read_at)
                          ? `Read from the portal on ${formatDate(caseItem.financas_waiting.read_at)}`
                          : 'Read from the portal'
                      }
                    >
                      Portal: {caseItem.financas_waiting.state}
                    </span>
                  )}
                  {/* What each one needs, on the tab that exists to answer
                      exactly that. Truncated to the column, in full on hover. */}
                  {showFinancasAsked && caseItem.financas_waiting?.asked_for && (
                    <span
                      className="case-financas-asked-for"
                      title={caseItem.financas_waiting.asked_for}
                    >
                      {caseItem.financas_waiting.asked_for}
                    </span>
                  )}
                </div>
              )}
              <div>
                <select className="status-select" value={caseItem.lawyer_status || 'Open'} onChange={(e) => handleStatusChange(caseItem.id, e.target.value)}>
                  {/* Moving a case in is done on its page. */}
                  {caseItem.lawyer_status === CERTIFICATE && <option value={CERTIFICATE} disabled>Certificate</option>}
                  <option value="Open">Open</option>
                  <option value={WAITING_ON_CUSTOMER}>Waiting on customer</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Complete">Complete</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div>
                <button className="download-button" onClick={() => handleDownloadFiles(caseItem.id)}>Download Files</button>
              </div>
            </div>
          ))
        ) : loadError ? (
          <p role="alert">Couldn't load cases. Refresh the page to try again.</p>
        ) : (
          <p>No cases to display.</p>
        )}
      </div>
      {submitDialogCase && (
        <SubmitToFinancasDialog
          caseId={submitDialogCase.id}
          clientName={[submitDialogCase.first_name, submitDialogCase.last_name].filter(Boolean).join(' ') || submitDialogCase.user_display_name || submitDialogCase.user_email}
          onCancel={() => setSubmitDialogCase(null)}
          onSubmitted={() => {
            setSubmitDialogCase(null);
            fetchCases();
          }}
        />
      )}
    </>
  );
};

export default CasesPage;
